import { useEffect, useRef } from "react";
import { Stack, useRouter } from "expo-router";
import { useCSSVariable, useResolveClassNames } from "uniwind";
import { useAuthContext } from "../hooks/use-auth-context";
import { authTrace } from "../lib/auth-trace";
import { useProfileSuspense } from "@/features/profile/api/profile";
import { PushRegistrar } from "@/features/notifications/components/push-registrar";

function useStackOptions() {
  const headerStyle = useResolveClassNames("bg-card");
  const contentStyle = useResolveClassNames("bg-background");
  const headerTintColor = useCSSVariable("--color-brand") as string;
  return {
    headerStyle: headerStyle as { backgroundColor?: string },
    headerTintColor,
    headerTitleStyle: {
      fontFamily: "ClashDisplay",
      fontWeight: "600",
    } as const,
    contentStyle: contentStyle as { backgroundColor?: string },
  };
}

function AuthenticatedNavigator({ userId }: { userId: string }) {
  const { data: profile } = useProfileSuspense(userId);
  const isProfileComplete = !!profile?.displayName;
  const screenOptions = useStackOptions();

  useEffect(() => {
    authTrace.navigated(isProfileComplete ? "tabs" : "complete-profile");
  }, [isProfileComplete]);

  return (
    <>
      {isProfileComplete && <PushRegistrar />}
      <Stack screenOptions={screenOptions}>
        <Stack.Protected guard={isProfileComplete}>
          <Stack.Screen name="(tabs)" options={{ headerShown: false }} />
          <Stack.Screen
            name="event/[eventId]"
            options={{ headerShown: false }}
          />
          <Stack.Screen name="user" options={{ headerShown: false }} />
        </Stack.Protected>
        <Stack.Protected guard={!isProfileComplete}>
          <Stack.Screen name="(auth)" options={{ headerShown: false }} />
        </Stack.Protected>
        <Stack.Screen name="+not-found" />
      </Stack>
    </>
  );
}

function UnauthenticatedNavigator() {
  const screenOptions = useStackOptions();

  useEffect(() => {
    authTrace.navigated("auth");
  }, []);

  return (
    <Stack screenOptions={screenOptions}>
      <Stack.Screen name="(auth)" options={{ headerShown: false }} />
      {/* Public event detail: logged-out visitors can open a shared link. */}
      <Stack.Screen name="event/[eventId]" options={{ headerShown: false }} />
      <Stack.Screen name="+not-found" />
    </Stack>
  );
}

export function RootNavigator() {
  const { isLoading, isLoggedIn, session } = useAuthContext();
  const router = useRouter();
  const wasLoggedIn = useRef<boolean | null>(null);

  useEffect(() => {
    if (!isLoading) {
      if (!isLoggedIn && wasLoggedIn.current === true) {
        router.replace("/(auth)");
      }
      wasLoggedIn.current = isLoggedIn;
    }
  }, [isLoading, isLoggedIn, router]);

  if (isLoading) return null;

  if (isLoggedIn && session?.user.id) {
    return <AuthenticatedNavigator userId={session.user.id} />;
  }

  return <UnauthenticatedNavigator />;
}
